import { useFieldArray, useFormContext } from 'react-hook-form'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { ControlledNumberInput } from '@fortem-monorepo/react-ui'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { FormError } from '@fortem-monorepo/react-ui'
import { SelectInput } from '@fortem-monorepo/react-ui'
import { FormDescription, FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import {
  FUNDING_INSTRUMENT_OPTIONS,
  FundingInstrument,
} from '@/pages/instruments/new-instrument/constants/funding-instruments'
import { BankLegSchemaType } from '@/pages/instruments/new-instrument/schemas/aswap.schema'

type Schema = {
  bank_legs: BankLegSchemaType[]
}

const SWAP_PAYMENT_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
  label: (index + 1)?.toString(),
  value: (index + 1)?.toString(),
}))

export interface BankLegFieldProps {}

export const BankLegsField: React.FC<BankLegFieldProps> = ({}) => {
  const { control, register, setValue } = useFormContext<Schema>()
  const { fields } = useFieldArray<Schema>({ name: 'bank_legs', control })

  return (
    <div>
      <FormField
        control={control}
        name="bank_legs"
        render={() => {
          return (
            <FormItem>
              <div className="space-y-2">
                <div className="flex gap-2 items-center">
                  <FormLabel>Bank Legs</FormLabel>
                  <FormError />
                </div>
                <FormDescription>Only single leg, same currency swaps for now.</FormDescription>
              </div>
              {fields.map((_, index) => {
                return (
                  <div className="flex flex-col gap-4 p-4 bg-white border rounded-md">
                    <FormFieldRow>
                      <FormField
                        name={`bank_legs.${index}.funding_instrument`}
                        control={control}
                        render={({ field: { value } }) => {
                          return (
                            <FormItem>
                              <div className="flex gap-2 items-center">
                                <FormLabel>Rate Instrument</FormLabel>
                                <FormError />
                              </div>
                              <SelectInput
                                className={'w-full'}
                                value={value}
                                onValueChange={(val) =>
                                  setValue(
                                    `bank_legs.${index}.funding_instrument`,
                                    val as FundingInstrument,
                                    {
                                      shouldValidate: true,
                                    },
                                  )
                                }
                                options={FUNDING_INSTRUMENT_OPTIONS}
                                placeholder={'Select a funding instrument...'}
                              />
                            </FormItem>
                          )
                        }}
                      />
                      <FormField
                        name={`bank_legs.${index}.pay_amount`}
                        control={control}
                        render={() => {
                          return (
                            <FormItem>
                              <div className="flex items-center gap-2 overflow-hidden">
                                <FormLabel>Pay Amount</FormLabel>
                                <FormError />
                              </div>
                              <ControlledNumberInput
                                thousandSeparator={','}
                                {...register(`bank_legs.${index}.pay_amount`)}
                                placeholder={'1,000,000'}
                              />
                            </FormItem>
                          )
                        }}
                      />
                      <FormField
                        name={`bank_legs.${index}.receive_amount`}
                        control={control}
                        render={() => {
                          return (
                            <FormItem>
                              <div className="flex items-center gap-2 overflow-hidden">
                                <FormLabel>Receive Amount</FormLabel>
                                <FormError />
                              </div>
                              <ControlledNumberInput
                                thousandSeparator={','}
                                {...register(`bank_legs.${index}.receive_amount`)}
                                placeholder={'1,500,00'}
                              />
                            </FormItem>
                          )
                        }}
                      />
                      <FormField
                        name={`bank_legs.${index}.floater_spread`}
                        control={control}
                        render={() => {
                          return (
                            <FormItem>
                              <div className="flex items-center gap-2 overflow-hidden">
                                <FormLabel>Receive Spread - Bps</FormLabel>
                                <FormError />
                              </div>
                              <ControlledNumberInput
                                thousandSeparator={','}
                                {...register(`bank_legs.${index}.floater_spread`)}
                                placeholder={'Enter the notional'}
                              />
                            </FormItem>
                          )
                        }}
                      />
                    </FormFieldRow>
                    <FormFieldRow>
                      <FormField
                        name={`bank_legs.${index}.swap_payments_yearly`}
                        control={control}
                        render={({ field: { value } }) => {
                          return (
                            <FormItem>
                              <div className="flex gap-2 items-center">
                                <FormLabel>Swap Payments Per Year</FormLabel>
                                <FormError />
                              </div>
                              <SelectInput
                                className={'w-full'}
                                value={value?.toString()}
                                options={SWAP_PAYMENT_OPTIONS}
                                onValueChange={(val) => {
                                  setValue(
                                    `bank_legs.${index}.swap_payments_yearly`,
                                    parseInt(val),
                                    { shouldValidate: true },
                                  )
                                }}
                              />
                            </FormItem>
                          )
                        }}
                      />
                      <FormField
                        name={`bank_legs.${index}.penultimate_floater_settlement_date`}
                        control={control}
                        render={({ field: { value } }) => {
                          return (
                            <FormItem>
                              <div className="flex gap-2 items-center">
                                <FormLabel>Penultimate Floater Sett Date</FormLabel>
                                <FormError />
                              </div>
                              <DatePicker
                                selected={value ? new Date(value) : undefined}
                                onSelect={(date) =>
                                  date &&
                                  setValue(
                                    `bank_legs.${index}.penultimate_floater_settlement_date`,
                                    toISO8601(date),
                                    { shouldValidate: true },
                                  )
                                }
                              />
                            </FormItem>
                          )
                        }}
                      />
                      <FormField
                        name={`bank_legs.${index}.cash_settlement_payment_date`}
                        control={control}
                        render={({ field: { value } }) => {
                          return (
                            <FormItem>
                              <div className="flex gap-2 items-center">
                                <FormLabel>Cash Settlement Payment Date</FormLabel>
                                <FormError />
                              </div>
                              <DatePicker
                                selected={value ? new Date(value) : undefined}
                                onSelect={(date) =>
                                  date &&
                                  setValue(
                                    `bank_legs.${index}.cash_settlement_payment_date`,
                                    toISO8601(date),
                                    { shouldValidate: true },
                                  )
                                }
                              />
                            </FormItem>
                          )
                        }}
                      />
                      <FormField
                        name={`bank_legs.${index}.first_floater_settlement_date`}
                        control={control}
                        render={({ field: { value } }) => {
                          return (
                            <FormItem>
                              <div className="flex gap-2 items-center">
                                <FormLabel>First Floater Sett Date</FormLabel>
                                <FormError />
                              </div>
                              <DatePicker
                                selected={value ? new Date(value) : undefined}
                                onSelect={(date) =>
                                  date &&
                                  setValue(
                                    `bank_legs.${index}.first_floater_settlement_date`,
                                    toISO8601(date),
                                    { shouldValidate: true },
                                  )
                                }
                              />
                            </FormItem>
                          )
                        }}
                      />
                    </FormFieldRow>
                  </div>
                )
              })}
            </FormItem>
          )
        }}
      />
    </div>
  )
}
